import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { toast } from "@shared/ui/use-toast";
import { AlertCircle, Lock } from "lucide-react";
import React, { useState } from "react";
import { DishonourModal } from "./DishonourModal";
import { TimelineTab } from "../../shared/TimelineTab";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useChequeDetails,
  useUpdateChequeStatus,
} from "@features/cx/paymentsv2/hooks";

export interface CheckDetailData {
  id: string;
  checkNo: string;
  checkRef: string;
  consumerName: string;
  consumerAcc: string;
  bankName: string;
  branchName: string;
  amount: string;
  penalty: string;
  checkDate: string;
  depositBy: string;
  source: string;
  status: string;
  hold: string;
  recordedAt?: string;
  lotId?: string;
  appliedTo?: string;
  collectedBy?: string;
  depositedAt?: string;
  clearedAt?: string;
  dishonouredReason?: string;
  penaltyWaived?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  data: CheckDetailData | null;
  onUpdateStatus: (id: string, newStatus: string) => void;
}

export const CheckDetailModal: React.FC<Props> = ({
  isOpen,
  onClose,
  data: summaryData,
  onUpdateStatus,
}) => {
  const [showDishonour, setShowDishonour] = useState(false);
  const remoteUtilityId = useRemoteUtilityId();

  const { data: detailResponse, isLoading } = useChequeDetails(
    Number(summaryData?.id),
    Number(remoteUtilityId),
  );

  const { mutate: updateStatus, isPending: isUpdatingStatus } =
    useUpdateChequeStatus();

  const data = React.useMemo(() => {
    if (!detailResponse && !summaryData) return null;
    const r = detailResponse || summaryData;
    return {
      id: r.id,
      checkNo: r.chequeNumber || "N/A",
      checkRef: r.checkRef || "N/A",
      consumerName: r.consumerName || "N/A",
      consumerAcc: r.consumerNo || "N/A",
      bankName: r.bank || "N/A",
      branchName: r.branch || "N/A",
      amount: r.amount || "N/A",
      penalty: r.penalty || "—",
      checkDate: r.chequeDate || "N/A",
      depositBy: r.depositBy || "N/A",
      depositStatus: r.depositStatus || "N/A",
      source: r.sourceId || "N/A",
      status: r.statusDisplay || "N/A",
      hold: r.disconnectHoldActive || false,
      recordedAt: r.chequeDate || "N/A",
      lotId: r.lotId || "N/A",
      appliedTo: r.appliedTo || "N/A",
      collectedBy: r.collectedBy || "N/A",
      depositedAt: r.depositedAt,
      clearedAt: r.clearedAt,
      dishonouredReason: r.dishonourReason || "N/A",
      penaltyWaived: r.penaltyWaived || "N/A",
    };
  }, [detailResponse, summaryData]);

  if (!data) return null;

  const handleCancelVoid = () => {
    onClose();
  };

  const handleMarkDeposited = () => {
    updateStatus(
      {
        chequeId: Number(data.id),
        action: "mark-deposited",
        payload: { remote_utility_id: Number(remoteUtilityId) },
      },
      {
        onSuccess: () => {
          onUpdateStatus(data.id, "Deposited");
          onClose();
        },
      },
    );
  };

  const handleMarkCleared = () => {
    updateStatus(
      {
        chequeId: Number(data.id),
        action: "mark-cleared",
        payload: { remote_utility_id: Number(remoteUtilityId) },
      },
      {
        onSuccess: () => {
          onUpdateStatus(data.id, "Cleared");
          onClose();
        },
      },
    );
  };

  const renderStatusPill = () => {
    let badgeColor = "bg-amber-100 text-amber-800 border-amber-200";
    if (data.status === "Cleared")
      badgeColor = "bg-green-100 text-green-800 border-green-200";
    if (data.status === "Dishonoured")
      badgeColor = "bg-red-100 text-red-800 border-red-200";
    return (
      <Badge variant="outline" className={badgeColor}>
        {data.status}
      </Badge>
    );
  };

  const renderHoldPill = () => {
    if (data.hold === true) {
      return (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-700 border-green-200 gap-1 pr-2"
        >
          <Lock className="w-3 h-3" /> Active Hold
        </Badge>
      );
    }
  };

  const leftAction = () => {
    if (data.status === "Provisional") {
      return (
        <Button variant="outline" onClick={handleCancelVoid}>
          Cancel
        </Button>
      );
    }
    return null;
  };

  const rightActions = () => {
    if (data.status === "Provisional") {
      return (
        <>
          <Button
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50"
            onClick={() => setShowDishonour(true)}
          >
            Mark Dishonoured
          </Button>
          <Button onClick={handleMarkDeposited} disabled={isUpdatingStatus}>
            Mark Deposited
          </Button>
        </>
      );
    }
    if (data.status === "Deposited") {
      return (
        <>
          <Button
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50"
            onClick={() => setShowDishonour(true)}
          >
            Mark Dishonoured
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700"
            onClick={handleMarkCleared}
            disabled={isUpdatingStatus}
          >
            Mark Cleared
          </Button>
        </>
      );
    }
    if (data.status === "Dishonoured") {
      return (
        <>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </>
      );
    }
    if (data.status === "Cleared") {
      return (
        <>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </>
      );
    }
    return (
      <Button variant="outline" onClick={onClose}>
        Close
      </Button>
    );
  };

  return (
    <>
      <Dialog open={isOpen && !showDishonour} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>
                Check {data.checkNo} · {data.amount}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-6 flex-1 overflow-y-auto pr-2">
            {/* Hero Section */}
            <div className="flex gap-4 items-start border p-4 rounded-lg bg-gray-50">
              <div className="mt-1">
                <AlertCircle className="w-8 h-8 text-gray-400" />
              </div>
              <div className="flex-1 space-y-2">
                <div className="font-bold text-base">
                  {data.consumerName} · {data.consumerAcc}
                </div>
                <div className="text-xs text-gray-500">
                  Check {data.checkRef} · {data.bankName} · {data.branchName} ·
                  Recorded {data.recordedAt || "N/A"}
                </div>
                <div className="flex gap-2 items-center">
                  {renderStatusPill()}
                  {renderHoldPill()}
                  {data.lotId && (
                    <Badge variant="secondary">Lot {data.lotId}</Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Info Grid */}
            <div className="border border-gray-200 rounded-lg p-5">
              <div className="text-xs font-semibold tracking-wider text-gray-500 mb-4">
                Check Details
              </div>
              <div className="grid grid-cols-2 gap-y-5 gap-x-10 text-sm">
                <ValueLabel label="Check Number" value={data.checkNo} />
                <ValueLabel
                  label="Amount"
                  value={<span className="font-bold">{data.amount}</span>}
                />
                <ValueLabel label="Check Date" value={data.checkDate} />
                <ValueLabel label="Source" value={data.source} />
                <ValueLabel
                  label="Collected By"
                  value={data.collectedBy || "N/A"}
                />
                {["Deposited", "Cleared"].includes(data.status) && (
                  <ValueLabel
                    label="Applied To"
                    value={data.appliedTo || "N/A"}
                  />
                )}
                {["Deposited", "Cleared"].includes(data.status) && (
                  <ValueLabel
                    label="Deposited At"
                    value={data.depositedAt || "N/A"}
                  />
                )}
                {data.status === "Cleared" && (
                  <ValueLabel
                    label="Cleared At"
                    value={
                      <span className="text-green-600">
                        {data.clearedAt || "N/A"}
                      </span>
                    }
                  />
                )}
                {data.status === "Dishonoured" && (
                  <>
                    <ValueLabel
                      label="Dishonoured"
                      value={
                        <span className="text-red-600">
                          {data.dishonouredReason || "R01 — Insufficient Funds"}
                        </span>
                      }
                    />
                    <ValueLabel
                      label="Penalty Posted"
                      value={
                        <span className="text-red-600">
                          {data.penalty !== "—" ? data.penalty : "$25.00"}
                        </span>
                      }
                    />
                  </>
                )}
              </div>
            </div>

            {/* Audit Trail */}
            <div className="border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="p-0 [&_.rounded-xl]:rounded-none [&_.border]:border-0 [&_.shadow-sm]:shadow-none bg-white">
                <TimelineTab
                  remoteUtilityId={remoteUtilityId}
                  requestId={data.id}
                  limit={3}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex justify-between items-center sm:justify-between pt-4 border-t">
            <div>{leftAction()}</div>
            <div className="flex gap-2">{rightActions()}</div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DishonourModal
        isOpen={showDishonour}
        onClose={() => {
          setShowDishonour(false);
          onClose();
        }}
        checkData={data}
        onDishonour={(penaltyData) => {
          onUpdateStatus(data.id, "Dishonoured");
          setShowDishonour(false);
          onClose();
        }}
      />
    </>
  );
};
